import React, { useState, useEffect } from 'react';
import masterApi from '../apis/master.api.js';


const Notifications = () => {
  const [notificationData, setNotificationData] = useState([]);
  const [newNotiCount, setNewNotiCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all'); // all | unread | read
  const [loading, setLoading] = useState(true);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const getAllNotifications = async () => {
    try {
      setLoading(true);
      const res = await masterApi.getAllNotifications();
      console.log("Notifications:", res.data.data);

      const { allNotifications, unreadCount } = res.data.data;
      
      setNotificationData(allNotifications || []);
      setNewNotiCount(unreadCount || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    if (newNotiCount === 0) return;
    if (!window.confirm('Mark all notifications as read?')) return;

    try {
      setMarkingAllRead(true);
      await masterApi.markAllRead();

      setNotificationData(prev => prev.map(notif => ({ ...notif, is_read: true })));
      setNewNotiCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      alert('Failed to mark notifications as read');
    } finally {
        getAllNotifications();
      setMarkingAllRead(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await masterApi.markSingleRead(id);

      setNotificationData(prev =>
        prev.map(notif => (notif.id === id ? { ...notif, is_read: true } : notif))
      );
      setNewNotiCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
    finally{
        getAllNotifications();
    }
  };

  useEffect(() => {
    getAllNotifications();
  }, []);

  const getFilteredNotifications = () => {
    if (activeTab === 'unread') return notificationData.filter(n => !n.is_read);
    if (activeTab === 'read')   return notificationData.filter(n => n.is_read);
    return notificationData;
  };

  const filtered = getFilteredNotifications();

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '24px 16px',
      fontFamily: 'Roboto, system-ui, sans-serif',
      backgroundColor: '#ffffff',
      color: '#333333'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        paddingBottom: '12px',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <h2 style={{
          margin: 0,
          fontSize: '1.75rem',
          fontWeight: 500,
          color: '#2e7d32'           // CashCrop green
        }}>
          Notifications
          {newNotiCount > 0 && (
            <span style={{
              backgroundColor: '#f44336', // red badge like bell notification
              color: '#ffffff',
              borderRadius: '12px',
              padding: '4px 10px',
              fontSize: '0.85rem',
              fontWeight: 600,
              marginLeft: '10px',
              verticalAlign: 'middle'
            }}>
              {newNotiCount}
            </span>
          )}
        </h2>

        {newNotiCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            disabled={markingAllRead || loading}
            style={{
              backgroundColor: markingAllRead || loading ? '#cccccc' : '#4caf50',
              color: '#ffffff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: markingAllRead || loading ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500,
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}
          >
            {markingAllRead ? 'Marking...' : 'Mark all as read'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #e0e0e0',
        marginBottom: '20px'
      }}>
        {['all', 'unread', 'read'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 28px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? '3px solid #2e7d32' : 'none',
              color: activeTab === tab ? '#2e7d32' : '#757575',
              fontWeight: activeTab === tab ? 600 : 500,
              fontSize: '1rem',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {tab === 'all' ? 'All' :
             tab === 'unread' ? (
               <>Unread {newNotiCount > 0 && (
                 <span style={{
                   backgroundColor: '#f44336',
                   color: 'white',
                   borderRadius: '10px',
                   padding: '3px 8px',
                   fontSize: '0.75rem',
                   marginLeft: '8px',
                   fontWeight: 600
                 }}>
                   {newNotiCount}
                 </span>
               )}</>
             ) : 'Read'}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#757575' }}>
          Loading notifications...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#757575', fontSize: '1.1rem' }}>
          {activeTab === 'all' ? "No notifications yet."
           : activeTab === 'unread' ? "No unread notifications."
           : "No read notifications."}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map((notif) => (
            <div
              key={notif.id}
              style={{
                padding: '16px',
                borderRadius: '8px',
                backgroundColor: notif.is_read ? '#ffffff' : '#f1f8e9', // light green unread bg
                border: notif.is_read ? '1px solid #e0e0e0' : '1px solid #a5d6a7',
                borderLeft: notif.is_read ? 'none' : '4px solid #4caf50',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '16px',
                transition: 'background-color 0.2s'
              }}
            >
              <div style={{ flex: 1 }}>
                <p style={{
                  margin: '0 0 8px 0',
                  fontSize: '1rem',
                  fontWeight: notif.is_read ? 400 : 500,
                  color: notif.is_read ? '#333' : '#1b5e20'
                }}>
                  {notif.message}
                </p>
                <span style={{ fontSize: '0.875rem', color: '#757575' }}>
                  {new Date(notif.createdAt).toLocaleString()}
                </span>
              </div>

              {!notif.is_read && (
                <button
                  onClick={() => handleMarkAsRead(notif._id)}
                  style={{
                    backgroundColor: '#66bb6a',
                    color: '#ffffff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Mark as read
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;